import {createSearchParams, useParams} from 'react-router-dom'
import {deleteRequest, getRequest} from '../../../services/base'
import Button from '../../common/Button'
import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {NavContext} from '../../../context/global_context'
import {ModuleContext, SELECT_MODULE, SET_MODULE_LIST,} from './context/context'
import InputText from '../../common/InputText'
import ModuleFormModal from './form'
import ModuleTable from './ModuleTable'
import {getSessionStorageData} from '../../../utils/SessionStorage'
import {ExclamationCircleOutlined} from '@ant-design/icons'
import {Flex, Form, Modal, Row, Space} from 'antd'
import useDebouncedValue from '../../../utils/common_function'
import {useIntl} from 'react-intl'
import { notifyError } from '../../../utils/notifications'

const ModuleList = () => {
  const intl = useIntl()
  let { vehicle } = useParams()

  const [state, dispatch] = useContext(ModuleContext)
  const [, navDispatch] = useContext(NavContext)

  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({})
  const currentUrl = useRef(`/api/modules/?vehicle=${vehicle}`)

  const [modal, contextHolder] = Modal.useModal()
  const debouncedSearch = useDebouncedValue(search, 500)

  const vehicle_redirect_url = getSessionStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
    : `/vehicles`

  useEffect(() => {
    navDispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header:
          intl.formatMessage({ id: 'layout.vehicleList' }) +
          ` / ${vehicle} / ` +
          intl.formatMessage({ id: 'sidebar.modules' }),
        id: vehicle,
        show_back_btn: true,
        dtc_details: [],
        back_btn_url: vehicle_redirect_url,
        full_width_container: false,
      },
    })
    getModulesData()
  }, [])

  useEffect(() => {
    currentUrl.current = `/api/modules/?vehicle=${vehicle}&search=${search}`
    getModulesData()
  }, [debouncedSearch])

  const getModulesData = async (url) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'modules.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })

    currentUrl.current = url ? url : currentUrl.current
    let res = await getRequest(currentUrl.current)
    setPagination(res.pagination)
    dispatch({ type: SET_MODULE_LIST, data: res.results })

    navDispatch({
      type: 'loader',
      data: {
        showLoader: false,
      },
      notification: {},
    })

    // After the loader dispatch above: a 'loader' action overwrites `notification`,
    // so notifying before it would be wiped out in the same React batch.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'modules.loadError' }))
    }
  }

  const buildUrl = ({ vehicle, search, limit, offset }) => {
    const params = new URLSearchParams({
      vehicle,
      search,
      limit,
      offset,
    })
    return `/api/modules/?${params.toString()}`
  }

  const deleteModule = async (record) => {
    let res = await deleteRequest(
      `/api/modules/delete/${record.acronym}/${vehicle}/`
    )
    if (res?.status) {
      const { currentPage, totalCount, totalPages } = pagination
      const pageSize = 10
      const newTotalCount = totalCount - 1
      const newTotalPages = Math.max(1, Math.ceil(newTotalCount / pageSize))
      const pageToFetch = Math.min(currentPage, newTotalPages)
      const offset = (pageToFetch - 1) * pageSize

      getModulesData(
        buildUrl({
          vehicle: vehicle,
          search: search,
          limit: pageSize,
          offset: offset,
        })
      )
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage(
            { id: 'modules.deleteSuccess' },
            { acronym: record.acronym }
          ),
        },
      })
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'modules.deleteError' }),
        },
      })
    }
  }

  const confirmDeleteModule = (record) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'modules.confirmDelete' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage(
        { id: 'modules.confirmDeleteContent' },
        { acronym: record.acronym }
      ),
      onOk: () => {
        deleteModule(record)
      },
      centered: true,
      maskClosable: true,
    })
  }

  const onEditModule = (record) => {
    dispatch({
      type: SELECT_MODULE,
      data: { selected_module: record, show_module_popup: true },
    })
  }

  const onCreateModule = () => {
    dispatch({
      type: SELECT_MODULE,
      data: { selected_module: {}, show_module_popup: true },
    })
  }

  const dataSource = useMemo(() => {
    if (!state?.module_list?.length) return []

    return state?.module_list?.map((module, index) => {
      const connectorNames = (module.connector || [])
        .map((c) => c.name)
        .filter(Boolean)
        .join(', ')

      return {
        ...module,
        key: `module-${index}`,
        rowKey: `module-${module.acronym}`,
        index: index,
        connector_names: connectorNames || '-',
      }
    })
  }, [state.module_list])

  return (
    <>
      {contextHolder}

      <div className="page sub-page">
        <Row className="body">
          <Form className="full-width">
            <Flex justify="space-between" gap={16} className="full-width">
              <InputText
                name="search_modules"
                type="search"
                placeholder={intl.formatMessage({
                  id: 'modules.searchPlaceholder',
                })}
                value={search}
                isRequired={false}
                wrapperClass="search-modules"
                onChange={(value) => {
                  setSearch(value)
                }}
              />

              <Space size="middle" align="center">
                <Button onClick={onCreateModule}>
                  {intl.formatMessage({ id: 'modules.createNew' })}
                </Button>
              </Space>
            </Flex>
          </Form>

          <Flex style={{ width: '100%' }}>
            <ModuleTable
              dataSource={dataSource}
              pagination={pagination}
              getModulesData={getModulesData}
              onEdit={onEditModule}
              onDelete={confirmDeleteModule}
            />
          </Flex>
        </Row>
      </div>

      {state.show_module_popup && (
        <ModuleFormModal
          getListData={() => {
            getModulesData()
          }}
        />
      )}
    </>
  )
}

export default ModuleList
